import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Eye, FileText, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getClientCandidates,
  getActiveEmployees,
  assignCandidateToEmployee,
} from '../../../services/admin/api';
import './ClientCandidates.css';

const AdminCandidates = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const heading = location.state?.heading || 'Admin Candidates';
  const companyName = location.state?.companyName || clientId;
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('');
  const [isAsc, setIsAsc] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc');
  const [assigningEmployeeId, setAssigningEmployeeId] = useState(null);

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.id.toString().includes(employeeSearchTerm.toLowerCase())
  );

  const filteredCandidates = [...candidates]
    .filter(candidate =>
      candidate.name.toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
      candidate.email.toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
      candidate.candidateid.toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
      candidate.bgcStatus.toLowerCase().includes(candidateSearchTerm.toLowerCase())
    )
    // .sort((a, b) => {
    //   if (!sortColumn) return 0;

    //   const valA = a[sortColumn]?.toString().toLowerCase() || '';
    //   const valB = b[sortColumn]?.toString().toLowerCase() || '';

    //   if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    //   if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    //   return 0;
    // });

    const fetchCandidates = async (clientId, page, recordsPerPage, sortColumn, isAsc) => {
      setLoadingCandidates(true);
      try {
        const response = await getClientCandidates(clientId, page, recordsPerPage, sortColumn, isAsc);

        if (typeof response === 'string') throw new Error(response);

        if (response && Array.isArray(response.content)) {
          setCandidates(response.content);
          setTotalPages(response.totalPages || 0);
          setTotalRecords(response.totalElements || 0);
        } else {
          setCandidates([]);
          setTotalPages(0);
        }
      } catch (error) {
        if (error?.response?.status === 503 || error?.message?.includes('Client service is unavailable')) {
          navigate('/admin/clients');
        } else {
          toast.error('Failed to fetch candidates.');
        }
      } finally {
        setLoadingCandidates(false);
      }
    };

  useEffect(() => {

    fetchCandidates(clientId, page, recordsPerPage, sortColumn, isAsc);
  }, [clientId, page, recordsPerPage, sortColumn, isAsc]);

  const renderSortArrow = (field) => {
    return (
      <span className="sort-icons">
        <span
          className={`arrow ${sortColumn === field && sortOrder === 'asc' ? 'active' : ''
            }`}
        >
          ↑
        </span>
        <span
          className={`arrow ${sortColumn === field && sortOrder === 'desc' ? 'active' : ''
            }`}
        >
          ↓
        </span>
      </span>
    );
  };

  const handleSort = async (column) => {
    let sort = !isAsc;
    setIsAsc(sort);
    setSortColumn(column)
    setSortOrder(sort ? 'asc' : 'desc');
    setPage(0)
    // await fetchCandidates(clientId, page, recordsPerPage, column, sort);

  };

  const handleSubmitAssignment = async (employeeId) => {
    if (!selectedCandidate) {
      toast.error('No candidate selected for assignment.');
      return;
    }

    setAssigningEmployeeId(employeeId);

    const payload = {
      candidateId: selectedCandidate.candidateid,
      employeeId,
      userid: selectedCandidate.userid,
      snapshot: {
        fullName: selectedCandidate.name,
        email: selectedCandidate.email,
        phone: selectedCandidate.mobileNumber || '',
      },
    };

    try {
      const res = await assignCandidateToEmployee(payload);
      toast.success(res.message || 'Candidate assigned successfully.');

      // const updatedCandidatesResponse = await getClientCandidates(clientId, page, recordsPerPage);
      // setCandidates(updatedCandidatesResponse.content || []);
      const updatedCandidatesResponse = await getClientCandidates(
        clientId,
        page,
        recordsPerPage,
        sortColumn,
        isAsc
      );
      setCandidates(updatedCandidatesResponse.content || []);

      setShowModal(false); // close modal directly
    } catch {
      toast.error('Failed to assign candidate.');
    } finally {
      setAssigningEmployeeId(null);
    }
  };


  const getStatusTextClass = (status) => {
    switch (status) {
      case 'COMPLETED': return 'admin-status-text green';
      case 'WORK_IN_PROGRESS': return 'admin-status-text orange';
      case 'YET_TO_START': return 'admin-status-text purple';
      case 'VERIFIED': return 'admin-status-text blue';
      case 'PENDING_SUBMISSION': return 'admin-status-text gray';
      default: return 'admin-status-text gray';
    }
  };

  const getButtonClass = (status) => {
    switch (status) {
      case 'COMPLETED': return 'admin-assign-btn green';
      case 'WORK_IN_PROGRESS': return 'admin-assign-btn orange';
      case 'YET_TO_START': return 'admin-assign-btn purple';
      case 'VERIFIED': return 'admin-assign-btn green';
      case 'PENDING_SUBMISSION': return 'admin-assign-btn gray';
      default: return 'admin-assign-btn gray';
    }
  };

  const getButtonLabel = (status) => {
    switch (status) {
      case 'WORK_IN_PROGRESS': return 'Reassign';
      case 'COMPLETED':
      case 'VERIFIED': return 'Completed';
      case 'YET_TO_START':
      case 'PENDING_SUBMISSION': return 'Assign';
      default: return 'Assign';
    }
  };

  const isButtonDisabled = (status) => {
    return status === 'COMPLETED' || status === 'VERIFIED';
  };

  const handleAssignClick = async (candidate) => {
    try {
      const response = await getActiveEmployees();
      setEmployees(response.data.content);
      setSelectedCandidate(candidate);
      setShowModal(true);
      setEmployeeSearchTerm('');
    } catch {
      toast.error('Failed to load employees.');
    }
  };

  const startRecord = page * recordsPerPage + 1;
  const endRecord = Math.min(startRecord + candidates.length - 1, (page + 1) * recordsPerPage);

  return (
    <div className="admin-candidates-wrapper">
      <header className="admin-header"></header>

      <main className="admin-main">
        <div className="client-admin-card">

          <div className="client-info-section">
            <p><strong>Client Name:</strong> {location.state?.clientName || 'N/A'}</p>
            <p><strong>Company Name:</strong> {companyName}</p>
            <p><strong>Client User ID:</strong> {clientId || 'N/A'}</p>
          </div>


          <div className="clinet-table-controls">

            <div className="entries-select">
              Show
              <select
                value={recordsPerPage}
                onChange={(e) => {
                  setRecordsPerPage(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              entries
            </div>

            <div className="client-search-input-wrapper">
              <input
                type="text"
                placeholder="Search by Candidate ID, Name or Email"
                value={candidateSearchTerm}
                onChange={(e) => {
                  setCandidateSearchTerm(e.target.value);
                  setPage(0);
                }}
              />
              <Search className="client-search-icon" size={18} />
            </div>

          </div>



          {/* Table */}
          <div className="admin-table-container">
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('candidateid')}>
                    Candidate ID {renderSortArrow('candidateid')}
                    {/* {sortColumn === 'candidateid' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
                  </th>

                  <th onClick={() => handleSort('fullname')}>
                    Name {renderSortArrow('fullname')}
                    {/* {sortColumn === 'fullname' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
                  </th>

                  <th onClick={() => handleSort('email')}>
                    Email {renderSortArrow('email')} 
                    {/* {sortColumn === 'email' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
                  </th>

                  <th onClick={() => handleSort('bgcStatus')}>BGC Status
                    {renderSortArrow('bgcStatus')}
                    {/* {sortColumn === 'bgcStatus' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
                  </th>
                  <th>Assign</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
  {loadingCandidates ? (
    <tr>
      <td colSpan="6" className="admin-loading-text">
  Loading candidates...
</td>

    </tr>
  ) : filteredCandidates.length === 0 ? (
    <tr>
      <td colSpan="6" style={{ textAlign: 'center' }}>
        No candidates found.
      </td>
    </tr>
  ) : (
    filteredCandidates.map((candidate, idx) => (
      <tr key={idx}>
        <td>{candidate.candidateid}</td>
        <td>{candidate.name}</td>
        <td>{candidate.email}</td>
        <td>
          <span className={getStatusTextClass(candidate.bgcStatus)}>
            {candidate.bgcStatus || 'Unknown'}
          </span>
        </td>
        <td>
          <button
            className={getButtonClass(candidate.bgcStatus)}
            onClick={() => handleAssignClick(candidate)}
            disabled={isButtonDisabled(candidate.bgcStatus)}
          >
            {getButtonLabel(candidate.bgcStatus)}
          </button>
        </td>
        <td className="action-icons">
          <button
            className="icon-btn"
            disabled={candidate.bgcStatus !== 'VERIFIED'}
            onClick={() =>
              navigate(`/admin/reports/${candidate.candidateid}`, {
                state: { candidate },
              })
            }
          >
            <FileText size={18} />
          </button>

          <button
            className="icon-btn"
            onClick={() =>
              navigate(`/admin/candidate-details/${candidate.candidateid}`, {
                state: { candidate },
              })
            }
          >
            <Eye size={18} />
          </button>
        </td>
      </tr>
    ))
  )}
</tbody>

            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">

            <div className="pagination-summary">
              Showing {startRecord} to {endRecord} of {totalRecords} entries
            </div>

          <div className="pagination-buttons-user">
  {/* Previous */}
  <button
    className="page-btn"
    onClick={() => setPage(p => Math.max(p - 1, 0))}
    disabled={page === 0}
  >
    Previous
  </button>

  {/* Page Numbers (from backend totalPages) */}
  {Array.from({ length: totalPages }, (_, index) => (
    <button
      key={index}
      className={`page-btn ${page === index ? "active" : ""}`}
      onClick={() => setPage(index)}
    >
      {index + 1}
    </button>
  ))}

  {/* Next */}
  <button
    className="page-btn"
    onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
    disabled={page >= totalPages - 1 || totalPages === 0}
  >
    Next
  </button>
</div>

          </div>

        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-assign-employee-modal">
              <>
                <h3>Assign Candidate</h3>
                <p>
                  <strong>Name:</strong> {selectedCandidate?.name} &nbsp; | &nbsp;
                  <strong>Email:</strong> {selectedCandidate?.email} &nbsp; | &nbsp;
                  <strong>Candidate ID:</strong> {selectedCandidate?.candidateid}
                </p>

                {/* Search Employees */}
                <div className="admin-glass-search-wrapper">
                  {/* <span className="admin-glass-search-icon">
                    <Search size={18} />
                  </span> */}

                  <input
                    type="text"
                    placeholder="Search by Employee ID or Name"
                    value={employeeSearchTerm}
                    onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                    className="admin-glass-search-input"
                  />
                </div>

                <div className="cc-admin-employee-table-wrapper">
                  <table className="admin-employee-selection-table-header">
                    <thead>
                      <tr>
                        <th>Agent ID</th>
                        <th>Agent Name</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                  </table>

                  <div className="admin-table-body-scroll-wrapper">
                    {filteredEmployees.length > 0 ? (
                      <table className="admin-employee-selection-table-body">
                        <tbody>
                          {filteredEmployees.map((emp) => (
                            <tr key={emp.id}>
                              <td>{emp.id}</td>
                              <td>{emp.name}</td>
                              <td>
                                <button
                                  className="admin-assign-btn admin-assign-confirm-modal"
                                  onClick={() => handleSubmitAssignment(emp.id)}
                                  disabled={assigningEmployeeId === emp.id}
                                >
                                  {assigningEmployeeId === emp.id ? 'Assigning..' : 'Assign'}
                                </button>


                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="admin-no-results-message">
                        No employees found.
                      </p>
                    )}
                  </div>
                </div>

                <div className="admin-modal-buttons">
                  <button
                    className="admin-assign-cancel"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
              </>
            
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCandidates;
